/** @format */

import React, { type FC } from "react";

import { httpsCallable } from "firebase/functions";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import {
  cloudFunctions,
  generateErrorMessage,
  generateLink,
  useAuthContext,
  useSchemaValidation,
} from "senf-shared";

import Box from "../../atoms/box/Box";
import type { AuthResetPasswordProps } from "./AuthResetPassword.types";

import Form from "../../molecules/form/Form";

import ModalActionButtons from "../../molecules/modalStack/ModalActionButtons";
import { useModals } from "../../molecules/modalStack/ModalProvider";

const AuthResetPassword: FC<AuthResetPasswordProps> = ({
  formikStore: formikStoreInitial,
}) => {
  const { t } = useTranslation();

  const { loading, setLoading } = useAuthContext();
  const { closeModal, openModal } = useModals();

  const onPasswordReset = httpsCallable(cloudFunctions, "onPasswordReset");

  const { email } = useSchemaValidation();

  const inputItems = [{ name: "email", type: "email", placeholder: "E-Mail" }];

  const formikStore = useFormik({
    initialValues: formikStoreInitial?.values,
    validationSchema: email,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  const options = {
    submitText: t("global:reset"),
    submitDisabled: !formikStore.isValid,
    submitLoading: loading === "reset",
    onSubmit: async () => {
      setLoading("reset");
      try {
        const res = await onPasswordReset({
          email: formikStore.values?.email,
          url: `${window.location.origin}/resetPassword`,
          continueUrl: window.location.href,
        })
        openModal(null, {
          id: "resetEmailSent",
          title: t("global:emailSent"),
          description: t("auth:emailSentDescription"),
          cancelText: "OK",
        });
        return res
      } catch (e) {
        openModal(null, {
          id: "resetEmailError",
          title: t(generateErrorMessage(e.message)),
          description: t("auth:tryAgainLater"),
          cancelText: "OK",
        });
      }
    },
  };

  return (
    <Box
      flexDirection="column"
      position="relative"
      zIndex={9999}
    >
      <Box
        gap="16px"
        flexDirection="column"
        width="100%"
        padding="20px"
      >
        <Form
          width="100%"
          inputItems={inputItems}
          formik={formikStore}
        />
      </Box>
      <ModalActionButtons {...options} />
    </Box>
  );
};

export default AuthResetPassword;
